import React, { useState } from 'react';

import { Link } from 'react-router-dom';

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ModeEditTwoToneIcon from '@mui/icons-material/ModeEditTwoTone';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Card,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Typography,
} from '@mui/material';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';

import { updateLabOfferImages } from '~/services/LabService';

import { buttonStates } from '../../Common/Forms/states/submitButton';
import Image from '../../Common/Image';

const tagButtonMessages: Record<string, string> = {
  [buttonStates.default]: 'Tag Images',
  [buttonStates.saving]: 'Tagging Images',
  [buttonStates.saved]: 'Images Tagged',
  [buttonStates.failed]: 'Failed to Tag Images',
};

const imageOptions = {
  width: 170,
};

type Props = {
  offer: {
    result: App.Offer;
  };
  onRefresh: () => void;
};

// >= the threshold, then run batch job to tag offer images
const BATCH_MODE_IMAGE_THRESHOLD = 20;

export default function ImagesPane({ offer, onRefresh }: Props) {
  const { id_salesforce_external, images } = offer.result;
  const [tagButtonState, setTagButtonState] = useState(buttonStates.default);
  const [isBatchInfoOpen, setIsBatchInfoOpen] = useState(false);
  const isTagButtonStateTagging = tagButtonState === buttonStates.saving;

  const handleTagImages = () => {
    // if image count >= 20, then run in batch
    const handleInBatch = images.length >= BATCH_MODE_IMAGE_THRESHOLD;

    setTagButtonState(buttonStates.saving);
    updateLabOfferImages(id_salesforce_external, handleInBatch)
      .then(() => {
        setTagButtonState(buttonStates.saved);
        if (handleInBatch) {
          setIsBatchInfoOpen(true);
        } else {
          // non batch, so onRefresh to load data
          onRefresh();
        }
      })
      .catch(() => {
        setTagButtonState(buttonStates.failed);
      });
  };

  const handleCloseBatchInfo = () => {
    setIsBatchInfoOpen(false);
    setTagButtonState(buttonStates.default);
  };

  return (
    <span>
      <h1>
        <Link to={'/edit-offers/' + id_salesforce_external + '/images'}>
          <IconButton aria-label="Edit">
            <ModeEditTwoToneIcon fontSize="large" />
          </IconButton>
        </Link>
        <Button variant="text" onClick={handleTagImages} disabled={isTagButtonStateTagging}>
          {tagButtonMessages[tagButtonState]}
        </Button>
      </h1>
      <Grid container spacing={2}>
        {images?.map((image) => (
          <Grid item key={image.id_image} sm={4}>
            <Card variant="outlined" sx={{ p: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                <Image className="img-responsive" publicId={image.id_cloudinary_external} options={imageOptions} />
              </Box>
              {image.title && (
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {image.title}
                </Typography>
              )}
              {image.tags && (
                <Accordion sx={{ mt: 1 }}>
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    aria-controls={`tags-${image.id_image}-content`}
                    id={`tags-${image.id_image}-header`}
                  >
                    <Typography variant="body2">tags</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Box
                      component="pre"
                      sx={{
                        m: 0,
                        p: 1,
                        bgcolor: 'background.default',
                        borderRadius: 1,
                        fontFamily: 'monospace',
                        fontSize: 12,
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                        maxHeight: 160,
                        overflow: 'auto',
                      }}
                    >
                      {JSON.stringify(image.tags, null, 2)}
                    </Box>
                  </AccordionDetails>
                </Accordion>
              )}
            </Card>
          </Grid>
        ))}
      </Grid>
      <Dialog open={isBatchInfoOpen} onClose={handleCloseBatchInfo} maxWidth="sm" fullWidth>
        <DialogTitle>Images are being tagged</DialogTitle>
        <DialogContent>
          <Alert severity="info">
            Tagging is queued because this offer has 20+ images. Tags will appear automatically once processing
            finishes.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button variant="contained" color="primary" onClick={handleCloseBatchInfo}>
            Got it
          </Button>
        </DialogActions>
      </Dialog>
    </span>
  );
}
